import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Button, Input, FormControl, FormLabel, Alert, Card, CardContent, Stack } from '@mui/joy';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';
import { directSupabase } from '../lib/supabaseClient';
import { isPathAllowedForRole, staffLandingPath } from '../hooks/useProfile';

const SupabaseLogin: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { signIn } = useSupabaseAuth();

  const requested = (location.state as { from?: string })?.from;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await signIn(email, password);
      const { data: sessionData } = await directSupabase.auth.getUser();
      const userId = sessionData.user?.id;
      let role: string | null = null;
      if (userId) {
        const { data: profile } = await directSupabase
          .from('profiles')
          .select('role')
          .eq('id', userId)
          .maybeSingle();
        role = profile?.role || null;
      }
      const dest = requested && isPathAllowedForRole(role, requested)
        ? requested
        : staffLandingPath(role);
      navigate(dest, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during authentication');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 400, mx: 'auto', mt: 4, p: 2 }}>
      <Card>
        <CardContent>
          <Typography level="h4" mb={2}>
            Sign In
          </Typography>

          {error && (
            <Alert color="danger" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <FormControl>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  value={email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                  required
                  placeholder="Enter your email"
                  autoComplete="username"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Password</FormLabel>
                <Input
                  type="password"
                  value={password}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                  required
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
              </FormControl>

              <Button
                type="submit"
                loading={loading}
                fullWidth
              >
                Sign In
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SupabaseLogin; 